import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'

type TopBarCtx = {
  subscribe: (fn: (content: ReactNode) => void) => () => void
  publish: (content: ReactNode) => void
}

const TopBarContext = createContext<TopBarCtx>({
  subscribe: () => () => {},
  publish: () => {},
})

export function TopBarProvider({ children }: { children: ReactNode }) {
  const listeners = useRef(new Set<(c: ReactNode) => void>())

  const ctx = useMemo<TopBarCtx>(() => ({
    subscribe: (fn) => {
      listeners.current.add(fn)
      return () => listeners.current.delete(fn)
    },
    publish: (content) => {
      listeners.current.forEach(fn => fn(content))
    },
  }), [])

  return <TopBarContext.Provider value={ctx}>{children}</TopBarContext.Provider>
}

/** Renders the current top-bar left content. Place this in the header bar. */
export function TopBarContent() {
  const [content, setContent] = useState<ReactNode>(null)
  const { subscribe } = useContext(TopBarContext)
  useEffect(() => subscribe(setContent), [subscribe])
  return <>{content}</>
}

/** Call in a page component to set what appears in the top bar's left slot. */
export function useTopBar(content: ReactNode): void {
  const { publish } = useContext(TopBarContext)
  useLayoutEffect(() => {
    publish(content)
    return () => publish(null)
  })
}
